import React, { useEffect } from 'react';
import './Sidebar.css';

/**
 * Sidebar Component - Slides in from the right
 * Used for forms, details panels, and complex content that benefits from more space
 */
const Sidebar = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  width = 'lg', // 'sm' (400px), 'md' (500px), 'lg' (600px), 'xl' (700px), 'full' (100%)
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  footer
}) => {
  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && closeOnEscape) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const widthClasses = {
    sm: 'sidebar-sm',
    md: 'sidebar-md',
    lg: 'sidebar-lg',
    xl: 'sidebar-xl',
    full: 'sidebar-full'
  };

  return (
    <div className="sidebar-overlay">
      {/* Backdrop */}
      <div className="sidebar-backdrop" onClick={closeOnBackdropClick ? onClose : undefined} />

      {/* Sidebar Panel */}
      <div className={`sidebar-panel ${widthClasses[width] || 'sidebar-lg'}`}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {showCloseButton && (
              <button
                onClick={onClose}
                className="sidebar-close-btn"
                aria-label="Close sidebar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            <div className="sidebar-title-wrapper">
              <h2 className="sidebar-title">{title}</h2>
              {subtitle && <p className="sidebar-subtitle">{subtitle}</p>}
            </div>
          </div>
        </div>

        {/* Body - scrollable content */}
        <div className="sidebar-body">
          {children}
        </div>

        {/* Footer - optional sticky footer */}
        {footer && (
          <div className="sidebar-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
